/**
 * Leaving for the sign-in page, carrying where the client was.
 *
 * The portal used to redirect with a bare `window.location.replace('/signin')`,
 * so a session that expired on an invoice sent the client back to the
 * dashboard and they had to find the invoice again. The SP app has carried a
 * `returnTo` for a while; this is its `goToSignIn`, with the client portal's
 * own routes.
 *
 * A full page load is what drops the page: `window.location` discards the
 * router's in-memory state, which is where `location.state.from` lives, so the
 * destination has to travel in the URL.
 */

/**
 * Routes that may not be returned to. Signing in to land back on the sign-in
 * page is a loop, and the one-time links — an invite, a magic link, an access
 * link, a password reset — are spent by the time the session ends, so sending
 * the client back to one strands them on a dead token.
 */
export const NON_RETURNABLE_ROUTES = [
  '/signin',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/set-password',
  '/update-profile',
  '/magic',
  '/invite',
  '/accesslink',
];

export const getSafeSignInReturnTo = (returnTo?: string | null): string | null => {
  if (!returnTo) return null;

  try {
    // Resolved against this origin so an absolute URL pointing somewhere else
    // cannot come back out of here as a destination.
    const url = new URL(returnTo, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    if (NON_RETURNABLE_ROUTES.includes(url.pathname)) return null;
    if (url.pathname === '/') return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
};

interface GoToSignInOptions {
  /** The session ended on its own, rather than the client asking to leave. */
  expired?: boolean;
}

export const buildSignInUrl = (returnTo?: string | null, options: GoToSignInOptions = {}) => {
  const params = new URLSearchParams();
  const safeReturnTo = getSafeSignInReturnTo(returnTo);
  if (safeReturnTo) params.set('returnTo', safeReturnTo);
  // The sign-in page says what happened. The portal used to raise a toast and
  // reload 100ms later, which its own comment admitted might never be seen.
  if (options.expired) params.set('sessionExpired', '1');

  const query = params.toString();
  return query ? `/signin?${query}` : '/signin';
};

export const goToSignIn = (returnTo?: string | null, options: GoToSignInOptions = {}) => {
  // `replace`, not `assign`: the page the client could not load does not belong
  // in their history, where Back would walk straight into it again.
  window.location.replace(buildSignInUrl(returnTo, options));
};

/** Where the client is now, in the shape `getSafeSignInReturnTo` expects. */
export const currentLocationAsReturnTo = (): string =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;
